import React from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import { Id } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '../../../ui/Command';
import { Button } from '../../../ui/Button';

export interface SelectAsyncListProps<T extends { _id: Id }> {
  mode: 'single' | 'multi';
  selectedItems: T[] | undefined;
  mainListOptions: T[];
  handleSelect: (item: T) => void;
  getItemId: (item: T) => Id;
  isSelected: (id: Id) => boolean;
  renderOptionRow: (item: T, isSelected: boolean) => React.ReactNode;
  listContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  setListRefReady: (ready: boolean) => void;
  loadMoreRef: React.RefObject<HTMLDivElement | null>;
  isSearching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  isLoading: boolean;
  optionsLength: number;
  emptyMessage: string;
  loadingMessage: string;
  noMoreItemsMessage: string;
  allowCreateNew: boolean;
  onCreateNewClick?: () => void;
  createNewLabel: string;
  selectedCountLabel: string;
}

export function SelectAsyncList<T extends { _id: Id }>({
  mode,
  selectedItems,
  mainListOptions,
  handleSelect,
  getItemId,
  isSelected,
  renderOptionRow,
  listContainerRef,
  setListRefReady,
  loadMoreRef,
  isSearching,
  isFetchingNextPage,
  hasNextPage,
  isLoading,
  optionsLength,
  emptyMessage,
  loadingMessage,
  noMoreItemsMessage,
  allowCreateNew,
  onCreateNewClick,
  createNewLabel,
  selectedCountLabel,
}: SelectAsyncListProps<T>) {
  return (
    <div
      ref={el => {
        listContainerRef.current = el;
        setListRefReady(!!el);
      }}
      className="overflow-y-auto overflow-x-hidden min-h-[200px] max-h-[300px] flex-1"
      style={{
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
      onWheel={e => e.stopPropagation()}>
      <Command
        shouldFilter={false}
        className="rounded-lg border-none shadow-none h-full overflow-visible">
        <CommandList className="overflow-visible focus:outline-none max-h-none">
          {mode === 'multi' && selectedItems && selectedItems.length > 0 && (
            <CommandGroup heading={selectedCountLabel}>
              {selectedItems.map((item: T) => {
                const id = getItemId(item);
                return (
                  <CommandItem
                    key={id.toString()}
                    value={`selected-${id.toString()}`}
                    onMouseDown={e => e.preventDefault()}
                    onSelect={() => handleSelect(item)}
                    className={cn('px-4 py-2 cursor-pointer', 'bg-gray-100')}>
                    {renderOptionRow(item, true)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          <CommandGroup>
            {mainListOptions.map((item: T) => {
              const id = getItemId(item);
              const selected = isSelected(id);
              return (
                <CommandItem
                  key={id.toString()}
                  value={id.toString()}
                  onMouseDown={e => e.preventDefault()}
                  onSelect={() => handleSelect(item)}
                  className={cn('px-4 py-2 cursor-pointer', selected && 'bg-gray-100')}>
                  {renderOptionRow(item, selected)}
                </CommandItem>
              );
            })}

            {!isSearching && mainListOptions.length === 0 && (
              <CommandEmpty className="px-4 py-6">
                <div className="flex flex-col items-center justify-center gap-3 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                    <Search className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600">{emptyMessage}</p>
                  {allowCreateNew && onCreateNewClick && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="h-8 px-3"
                      onClick={e => {
                        e.stopPropagation();
                        onCreateNewClick();
                      }}>
                      <Plus className="w-4 h-4 mr-1" />
                      {createNewLabel}
                    </Button>
                  )}
                </div>
              </CommandEmpty>
            )}

            {isFetchingNextPage && (
              <div className="px-4 py-3 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{loadingMessage}</span>
              </div>
            )}

            {!hasNextPage && !isLoading && optionsLength > 0 && (
              <div className="px-4 py-3 text-center text-sm text-gray-400">
                {noMoreItemsMessage}
              </div>
            )}

            {hasNextPage && !isSearching && !isFetchingNextPage && (
              <div ref={loadMoreRef} className="h-4" />
            )}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}
